/**
 * calendar-range — which days a calendar range covers, and how it steps.
 *
 * FEAT-087 / S-369. The handoff is emphatic that every range must read from
 * **one** date-keyed source: an earlier version kept separate week and month
 * arrays that drifted apart, so a piece showed in one view and not another.
 * The view already builds that one map (publication date → pieces); this
 * module supplies only the *keys* each range asks it for, so Week, 4 weeks
 * and Month cannot disagree about what a day contains — they are all reading
 * the same map with different lists of dates.
 *
 * Pure and React-free so the date arithmetic is tested rather than trusted.
 * All arithmetic is local-time and goes through `addDays`, which steps the
 * calendar date rather than adding 24h — the two differ on DST boundaries,
 * and adding hours silently loses or repeats a day twice a year.
 */

export type CalendarMode = "day" | "week" | "fourweeks" | "month";

/** Selector order, narrowest first. */
export const CALENDAR_MODES: readonly CalendarMode[] = [
  "day",
  "week",
  "fourweeks",
  "month",
] as const;

export const CALENDAR_MODE_LABEL: Record<CalendarMode, string> = {
  day: "Day",
  week: "Week",
  fourweeks: "4 weeks",
  month: "Month",
};

export const DEFAULT_CALENDAR_MODE: CalendarMode = "month";

/**
 * A stored value from an older build (or a hand-edited one) must never wedge
 * the view, so anything unrecognised falls back to the default.
 */
export function parseCalendarMode(raw: string | null | undefined): CalendarMode {
  return CALENDAR_MODES.includes(raw as CalendarMode)
    ? (raw as CalendarMode)
    : DEFAULT_CALENDAR_MODE;
}

/** Local-time YYYY-MM-DD. Never `toISOString()`, which is UTC and shifts the day. */
export function localDateIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Calendar-day arithmetic, so a DST change does not add or drop a day. */
export function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

/** Sunday-first, matching the grid's day-of-week header. */
export function startOfWeek(d: Date): Date {
  return addDays(d, -d.getDay());
}

export function startOfMonth(d: Date): Date {
  const next = new Date(d);
  next.setDate(1);
  return next;
}

/**
 * The days a range covers, in order, as ISO keys.
 *
 *   day        one day
 *   week       7 days from the containing Sunday
 *   fourweeks  this week first, then the three after — 28 days. It has no
 *              month edge to fall off, which is the point of the range.
 *   month      whole weeks covering the month, so adjacent-month days show
 *              their real work instead of being blanked.
 */
export function rangeDays(anchor: Date, mode: CalendarMode): string[] {
  switch (mode) {
    case "day":
      return [localDateIso(anchor)];
    case "week": {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => localDateIso(addDays(start, i)));
    }
    case "fourweeks": {
      const start = startOfWeek(anchor);
      return Array.from({ length: 28 }, (_, i) => localDateIso(addDays(start, i)));
    }
    case "month": {
      const first = startOfMonth(anchor);
      const start = startOfWeek(first);
      const days: string[] = [];
      let cursor = start;
      // Whole weeks until the cursor has passed the month's last day.
      const monthIndex = first.getMonth();
      do {
        for (let i = 0; i < 7; i++) {
          days.push(localDateIso(cursor));
          cursor = addDays(cursor, 1);
        }
      } while (cursor.getMonth() === monthIndex);
      return days;
    }
  }
}

/** The four weeks of the 4-weeks range, each as its own 7-day list. */
export function fourWeekRows(anchor: Date): string[][] {
  const days = rangeDays(anchor, "fourweeks");
  return [0, 1, 2, 3].map((w) => days.slice(w * 7, w * 7 + 7));
}

/**
 * Moving one range forward or back. A range steps by its own size — a week
 * moves seven days, not one — so paging never skips or repeats days.
 */
export function stepAnchor(d: Date, mode: CalendarMode, dir: 1 | -1): Date {
  switch (mode) {
    case "day":
      return addDays(d, dir);
    case "week":
      return addDays(d, 7 * dir);
    case "fourweeks":
      return addDays(d, 28 * dir);
    case "month": {
      // setMonth on the 31st of a 31-day month would roll into the next
      // month; anchoring to the 1st first keeps stepping stable.
      const next = startOfMonth(d);
      next.setMonth(next.getMonth() + dir);
      return next;
    }
  }
}
